import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../utils/useAuth";
import { db } from "../firebase";
import { doc, updateDoc } from "firebase/firestore";

export default function RefereeAvailability() {
  const { user, profile } = useAuth();
  
  // Check if BokSmart is valid
  const isBoksmartValid = () => {
    if (!profile?.boksmartExpiry) return false;
    const expiryDate = new Date(profile.boksmartExpiry);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    return expiryDate >= today;
  };

  const boksmartValid = isBoksmartValid();
  
  // Generate initial state for 3 weeks
  const getInitialAvailability = () => {
    const initial = {};
    for (let week = 1; week <= 3; week++) {
      ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].forEach(day => {
        initial[`week${week}_${day}`] = "unavailable";
      });
    }
    return initial;
  };

  const [availability, setAvailability] = useState(getInitialAvailability());

  // Load existing availability from profile
  useEffect(() => {
    if (profile?.availability) {
      setAvailability({ ...getInitialAvailability(), ...profile.availability });
    }
  }, [profile]);

  function handleChange(key, value) {
    setAvailability((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  async function save() {
    try {
      await updateDoc(doc(db, "users", user.uid), {
        availability,
        updatedAt: new Date(),
      });
      alert("Availability updated!");
    } catch (err) {
      alert("Error saving availability: " + err.message);
    }
  }

  const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
  const weeks = [1, 2, 3];

  // Calculate actual dates for each week
  const getWeekDates = (weekNum) => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay() + 1 + (weekNum - 1) * 7); // Monday of the week
    
    return days.map((day, index) => {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + index);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    });
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6">3-Week Availability</h2>

      {/* BokSmart Expiry Warning */}
      {!boksmartValid && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          <p className="font-bold">⚠️ BokSmart Certification Expired or Missing</p>
          <p className="text-sm mt-1">
            {profile?.boksmartExpiry 
              ? `Your BokSmart certification expired on ${new Date(profile.boksmartExpiry).toLocaleDateString()}. Please update your profile with a valid BokSmart expiry date to edit your availability.`
              : "You must add a BokSmart expiry date to your profile before you can set your availability."}
          </p>
          <p className="text-sm mt-2">
            <Link to="/profile" className="underline font-semibold">Go to Profile →</Link>
          </p>
        </div>
      )}

      <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 ${!boksmartValid ? 'opacity-50 pointer-events-none' : ''}`}>
        {weeks.map((weekNum) => {
          const weekDates = getWeekDates(weekNum);
          
          return (
            <div key={weekNum} className="bg-gray-50 p-4 rounded-lg shadow">
              <h3 className="text-xl font-bold mb-4 text-blue-700">Week {weekNum}</h3>
              
              <div className="space-y-3">
                {days.map((day, dayIndex) => {
                  const key = `week${weekNum}_${day}`;
                  const value = availability[key];
                  
                  return (
                    <div key={key} className="bg-white p-2 rounded shadow">
                      <div className="text-sm font-semibold capitalize mb-1">{day}</div>
                      <div className="text-xs text-gray-500 mb-2">({weekDates[dayIndex]})</div>

                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => handleChange(key, "available")}
                          className={`flex-1 py-1 rounded font-bold text-sm ${
                            value === "available" 
                              ? "bg-green-600 text-white" 
                              : "bg-gray-200 text-gray-400"
                          }`}
                        >
                          ✓
                        </button>
                        
                        <button
                          type="button"
                          onClick={() => handleChange(key, "unavailable")}
                          className={`flex-1 py-1 rounded font-bold text-sm ${
                            value === "unavailable" 
                              ? "bg-red-600 text-white" 
                              : "bg-gray-200 text-gray-400"
                          }`}
                        >
                          ✕
                        </button>
                        
                        <button
                          type="button"
                          onClick={() => handleChange(key, "maybe")}
                          className={`flex-1 py-1 rounded font-bold text-sm ${
                            value === "maybe" 
                              ? "bg-black text-white" 
                              : "bg-gray-200 text-gray-400"
                          }`}
                        >
                          ?
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <button
        onClick={save}
        disabled={!boksmartValid}
        className={`mt-6 w-full p-3 rounded text-lg font-semibold ${
          boksmartValid 
            ? 'bg-green-700 text-white hover:bg-green-800' 
            : 'bg-gray-400 text-gray-600 cursor-not-allowed'
        }`}
      >
        {boksmartValid ? 'Save Availability' : 'BokSmart Required to Save'}
      </button>
    </div>
  );
}